import { TLPayload, TermLifeInsuranceWizard } from "@earned/wizard/dist";
import { NoCacheWizardExitMessage } from "@modules/tasks/constants";
import { useWizardCloseConfirm } from "@modules/tasks/hooks.web";
import { useUpdateTermLifeInsuranceInfo } from "@modules/tasks/mutations";
import { useTermLifeInsuranceInfo } from "@modules/tasks/queries";
import useTasksStore from "@modules/tasks/state";
import { TaskTemplateId, WizardId } from "@modules/tasks/types";
import * as React from "react";

interface TermLifeInsuranceWizardControllerProps {
  clients: Forme.ClientUser[];
}

const TermLifeInsuranceWizardController: React.FC<
  TermLifeInsuranceWizardControllerProps
> = ({ clients }) => {
  const {
    wizardId,
    setWizardId,
    isWizardOpen,
    wizardTask,
    setWizardTask,
    setRemoteCompleteTaskId,
  } = useTasksStore();
  const client = React.useMemo(() => {
    return clients.filter((c) =>
      c.isPrimary
        ? wizardTask?.templateId ===
          TaskTemplateId.UPDATE_TERM_LIFE_INSURANCE_INFORMATION
        : wizardTask?.templateId ===
          TaskTemplateId.UPDATE_TERM_LIFE_INSURANCE_INFORMATION_COCLIENT
    )[0] as Forme.ClientUser;
  }, [clients, wizardTask]);
  const { termLifeInsuranceInfo, isLoading } = useTermLifeInsuranceInfo(
    client.id
  );
  const {
    mutateAsync,
    isLoading: isSubmitting,
    isSuccess,
    reset,
  } = useUpdateTermLifeInsuranceInfo(client.id);

  const isOpen = React.useMemo(
    () => wizardId === WizardId.TERM_LIFE_INSURANCE && isWizardOpen,
    [wizardId, isWizardOpen]
  );

  const { openCloseConfirmDialog, CloseConfirmDialogComponent } =
    useWizardCloseConfirm({
      onConfirmClose: reset,
      dialogMessage: NoCacheWizardExitMessage,
    });

  const handleSubmit = React.useCallback(
    async (payload: TLPayload) => {
      await mutateAsync({ data: payload });
    },
    [mutateAsync]
  );

  const handleDone = React.useCallback(async () => {
    if (wizardTask) setRemoteCompleteTaskId(wizardTask.id);
    setWizardTask(null);
    setWizardId(null);
    reset();
  }, [reset, setWizardId, setWizardTask, setRemoteCompleteTaskId, wizardTask]);

  return (
    <>
      <TermLifeInsuranceWizard
        infoOwnerName={
          clients.length === 1
            ? undefined
            : `${client.firstName} ${client.lastName}`
        }
        isOpen={isOpen}
        close={openCloseConfirmDialog}
        onSubmit={handleSubmit}
        isSuccess={isSuccess}
        isSubmitting={isSubmitting}
        isLoading={isLoading}
        onDone={handleDone}
        cache={termLifeInsuranceInfo}
        modalRootSelector="#wizard-root"
      />
      <CloseConfirmDialogComponent />
    </>
  );
};

export default TermLifeInsuranceWizardController;
